import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { isEqual } from 'lodash-es';
import { Store } from '@ngxs/store';
import { EMPTY, fromEventPattern, NEVER, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, skip, skipWhile, switchMap } from 'rxjs/operators';
import { MenuActions } from '@nimbus-library/features/menu/state/menu.actions';
import { MenuState } from '@nimbus-library/features/menu/state/menu.state';
import { NavigationActions } from '@nimbus-library/features/navigation/navigation.actions';
import { CacheService } from '@nimbus-library/features/cache';
import { ModalService } from '@nimbus-library/features/modal/services/modal.service';
import { LocalizationActions } from '@nimbus-library/features/localization/store/localization.action';
import { TranslationsReadyService } from '@nimbus-library/features/localization/services/translations-ready.service';
import { IGeoLocationService } from '@nimbus-global/features/geo-location/services/i-geo-location.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { TrackingService } from '@nimbus-library/features/tracking/tracking.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { CommonThemeActions } from '@nimbus-library/features/shared/store/theme-store/common-theme.actions';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { NgxsStateLoggerService } from '@nimbus-library/features/logger/services/state/ngxs-state.logger';
import { AppUpdateService } from '@nimbus-global/features/shared/services/app-update.service';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';
import { startPeriodicAction } from './common/functions/start-periodic-action.function';

@Component({
  standalone: false,
  selector: 'nimbus-app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly subscription$: Subscription;
  public showMenu$: Observable<boolean>;
  public companionChatActive$: Observable<boolean>;
  public translationsReady$: Observable<boolean>;

  public constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly logger: ILogger,
    private readonly authenticationService: IAuthenticationService,
    private readonly cacheService: CacheService,
    private readonly modalService: ModalService,
    private readonly stateLogger: NgxsStateLoggerService,
    private readonly geoLocationService: IGeoLocationService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly trackingService: TrackingService,
    public readonly applicationElement: ElementRef,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
    private readonly translationsReadyService: TranslationsReadyService,
    appUpdateService: AppUpdateService,
  ) {
    this.subscription$ = new Subscription();
    appUpdateService.initialize();
  }

  public ngOnInit(): void {
    this.showMenu$ = this.store.select(MenuState.show);
    this.companionChatActive$ = this.companionSplitScreenService.active$;
    this.translationsReady$ = this.translationsReadyService.translationsReady$;
    this.permissionManagerService.initialize();
    this.geoLocationService.resolveSelectedLocation();

    this.store.dispatch(new MenuActions.Initialize());
    this.store.dispatch(new LocalizationActions.LoadLanguage());
    this.store.dispatch(new LocalizationActions.LoadDateFormat());
    this.store.dispatch(new CommonThemeActions.Initialize());

    this.subscription$.add(this.subscribeOnUserIsAuthenticated());
    this.subscription$.add(this.startCheckPermissionsChanges());
    this.subscription$.add(this.startTrackNavigation());
    this.subscription$.add(this.startManageCache());
    this.subscription$.add(this.startListenLogging());
    this.subscription$.add(new Subscription(() => this.modalService.ngOnDestroy()));
    this.trackingService.initialize();
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private startCheckPermissionsChanges(): Subscription {
    return this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) => {
          if (isAuthenticated) {
            return this.permissionManagerService.getPermissionsModel$().pipe(
              filter((p) => !!p),
              skip(1), // first always the global model needs to be skiped from the local permission comparison
              distinctUntilChanged((p1, p2) => isEqual(p1, p2)),
              pairwise(),
              filter(([p1, p2]) => !isEqual(p1, p2)),
            );
          }

          return EMPTY;
        }),
      )
      .subscribe(() => this.router.navigate([], { queryParams: { forceReload: true }, queryParamsHandling: 'merge' }));
  }

  private startTrackNavigation(): Subscription {
    return startPeriodicAction(
      this.store,
      new NavigationActions.StartTrackNavigation(),
      new NavigationActions.StopTrackNavigation(),
    ).subscribe();
  }

  private subscribeOnUserIsAuthenticated(): Subscription {
    return this.authenticationService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        const userInfo = this.authenticationService.getUserInfo();
        this.logger.setUser(userInfo.userId, userInfo.userName);
      } else {
        this.logger.resetUser();
      }
    });
  }

  private startListenLogging(): Subscription {
    return this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) => {
          if (isAuthenticated) {
            return fromEventPattern(
              () => this.stateLogger.startListening(),
              () => this.stateLogger.stopListening(),
            );
          }
          return NEVER;
        }),
      )
      .subscribe();
  }

  private startManageCache(): Subscription {
    return this.authenticationService.isAuthenticated$
      .pipe(
        skipWhile((isAuthenticated) => !isAuthenticated),
        distinctUntilChanged(),
        pairwise(),
        map(([, isAuthenticated2]) => isAuthenticated2),
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.cacheService.persist();
        } else {
          this.cacheService.unPersist();
        }
      });
  }
}
